import {
  ComponentType,
  ReactNode,
  ButtonHTMLAttributes,
  forwardRef,
} from "react";
import { Loader2 } from "lucide-react";
import { twMerge } from "tailwind-merge";

type IconComponent = ComponentType<{
  className?: string;
  strokeWidth?: number;
}>;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "contained" | "outlined" | "ghost";
  color?: "primary" | "secondary" | "blue" | "navy" | "danger";
  size?: "sm" | "md" | "lg";
  icon?: IconComponent;
  iconStrokeWidth?: number;
  loading?: boolean;
  children?: ReactNode;
  className?: string;
}

const containedColorClasses: Record<
  NonNullable<ButtonProps["color"]>,
  string
> = {
  primary:
    "bg-content-primary text-white hover:bg-content-emphasis transition-colors duration-150",
  secondary:
    "bg-brand-gray text-content-muted hover:bg-surface-hover transition-colors duration-150",
  blue: "bg-brand-blue text-white hover:opacity-90 transition-opacity duration-150",
  navy: "bg-brand-navy text-white hover:bg-brand-navy-hover transition-colors duration-150",
  danger:
    "bg-[#CE2C31] text-white hover:bg-destructive-hover transition-colors duration-150",
};

const containedDisabledClasses: Record<
  NonNullable<ButtonProps["color"]>,
  string
> = {
  primary: "bg-content-disabled! text-white!",
  secondary: "bg-content-disabled! text-white!",
  blue: "bg-content-disabled! text-white!",
  navy: "bg-content-disabled! text-white!",
  danger: "bg-content-disabled! text-white!",
};

const outlinedColorClasses: Record<
  NonNullable<ButtonProps["color"]>,
  string
> = {
  primary:
    "bg-white text-status-neutral-content hover:bg-surface-hover transition-colors duration-150 border-1",
  secondary:
    "bg-white text-status-neutral-content hover:bg-surface-hover transition-colors duration-150 border-1",
  blue: "bg-white text-status-neutral-content hover:bg-surface-hover transition-colors duration-150 border-1",
  navy: "bg-white text-brand-navy hover:bg-brand-navy-wash transition-colors duration-150 border-1 border-brand-navy",
  danger:
    "bg-white text-[#CE2C31] hover:bg-destructive-wash transition-colors duration-150 border-1 border-destructive-base",
};
const outlinedDisabledClasses = "text-content-tertiary border-transparent";

const ghostColorClasses: Record<NonNullable<ButtonProps["color"]>, string> = {
  primary:
    "bg-transparent text-status-neutral-content hover:bg-surface-hover transition-colors duration-150",
  secondary:
    "bg-transparent text-status-neutral-content hover:bg-surface-hover transition-colors duration-150",
  blue: "bg-transparent text-status-neutral-content hover:bg-surface-hover transition-colors duration-150",
  navy: "bg-transparent text-brand-navy hover:bg-brand-navy-wash transition-colors duration-150",
  danger:
    "bg-transparent text-[#CE2C31] hover:bg-destructive-wash transition-colors duration-150",
};

const sizeClasses: Record<
  NonNullable<ButtonProps["size"]>,
  { root: string; iconOnly: string; iconSize: string; text: string }
> = {
  sm: {
    root: "h-6 rounded-2xs",
    iconOnly: "w-6",
    iconSize: "size-3",
    text: "px-2 gap-1 text-[11px] font-medium",
  },
  md: {
    root: "h-7 rounded-control",
    iconOnly: "w-7",
    iconSize: "size-3.5",
    text: "px-2.5 gap-1.5 text-[12px] font-medium",
  },
  lg: {
    root: "rounded-lg",
    iconOnly: "size-8",
    iconSize: "size-[15px]",
    text: "p-3 gap-2 font-semibold text-[13px] leading-5",
  },
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = "contained",
      color = "primary",
      size = "md",
      icon: Icon,
      loading,
      children,
      className,
      iconStrokeWidth,
      ...props
    },
    ref,
  ) {
    const isDisabled = props.disabled || loading;
    const isIconOnly = Icon && !children;
    const s = sizeClasses[size];

    const activeClasses =
      variant === "ghost"
        ? ghostColorClasses[color]
        : variant === "outlined"
          ? outlinedColorClasses[color]
          : containedColorClasses[color];

    const disabledClasses =
      variant === "contained"
        ? containedDisabledClasses[color]
        : outlinedDisabledClasses;

    return (
      <button
        type="button"
        {...props}
        ref={ref}
        disabled={isDisabled}
        className={twMerge(
          "flex shrink-0 cursor-pointer items-center justify-center border-0 outline-none",
          activeClasses,
          s.root,
          isIconOnly ? s.iconOnly : s.text,
          className,
          isDisabled && `pointer-events-none ${disabledClasses}`,
        )}
      >
        {loading ? (
          <Loader2 className={twMerge(s.iconSize, "animate-spin")} />
        ) : (
          Icon && (
            <Icon
              className={twMerge(s.iconSize, "cursor-pointer")}
              strokeWidth={iconStrokeWidth ?? 2.5}
            />
          )
        )}
        {children}
      </button>
    );
  },
);
